import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { type FormEvent, useState } from "react";
import { EPOCUS_TEXT_SECONDARY, rem } from "../../_lib/epocus-palette";
import { useEpocusAdminCreateProgramMutation } from "./__generated__/epocus-admin-operations.gql";
import { CreatedProgramCallout } from "./created-program-callout";

type CreatedProgram = {
  id: string;
  name: string;
  dimseAeTitle?: string | null;
};

const Fields = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const HelperCaption = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

export const CreateProgramDialog: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const [name, setName] = useState("");
  const [elephantFacilityId, setElephantFacilityId] = useState("");
  const [nameError, setNameError] = useState<string>();
  const [created, setCreated] = useState<CreatedProgram>();

  const [createProgram, { loading }] = useEpocusAdminCreateProgramMutation({
    onCompleted: ({ createEpocusProgram }) => setCreated(createEpocusProgram),
    onError: (e) => toast.error(`Couldn't create program: ${e.message}`),
    refetchQueries: ["EpocusAdminPrograms"],
  });

  const reset = () => {
    setName("");
    setElephantFacilityId("");
    setNameError(undefined);
    setCreated(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Program name is required");
      return;
    }
    setNameError(undefined);

    const trimmedFacilityId = elephantFacilityId.trim();
    void createProgram({
      variables: {
        input: {
          name: trimmedName,
          elephantFacilityId: trimmedFacilityId || undefined,
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{created ? "Program created" : "New program"}</DialogTitle>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent>
          {created ? (
            <CreatedProgramCallout
              programId={created.id}
              name={created.name}
              dimseAeTitle={created.dimseAeTitle}
            />
          ) : (
            <Fields>
              <TextField
                id="epocus-admin-program-name"
                label="Program name"
                required
                autoFocus
                variant="outlined"
                fullWidth
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(undefined);
                }}
                error={!!nameError}
                helperText={nameError}
              />
              <Box display="flex" flexDirection="column">
                <TextField
                  id="epocus-admin-program-facility-id"
                  label="ElephantOS facility ID"
                  variant="outlined"
                  fullWidth
                  value={elephantFacilityId}
                  onChange={(e) => setElephantFacilityId(e.target.value)}
                />
                <HelperCaption>
                  Optional. Only for deployments where ePOCUS runs alongside
                  ElephantOS — leave blank for a standalone program.
                </HelperCaption>
              </Box>
            </Fields>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} style={{ textTransform: "none" }}>
            {created ? "Done" : "Cancel"}
          </Button>
          {!created && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Create program
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
